/* eslint-disable camelcase */
import { motion, AnimatePresence } from 'framer-motion';
import PropTypes from 'prop-types';
import 'sal.js/dist/sal.css';
import React, { useEffect, useState, Fragment } from 'react';
import sal from 'sal.js';

import ResImg from 'components/resImg';
import ResVideo from 'components/resVideo';
import CtaCard from 'components/shared/cta-card';
import PlusIcon from 'components/shared/icons/plus-icon';

const AllPeople = ({ blok }) => {
  const [activePeople, setActivePeople] = useState(blok.people);
  const [filter, setFilter] = useState(undefined);
  const [showLocation, setShowLocation] = useState(false);
  const [activeFilter, setActiveFilter] = useState(false);

  const groupedPeople = activePeople.reduce((acc, item) => {
    if (!acc[item.content?.group_type]) {
      acc[item.content?.group_type] = [];
    }
    acc[item.content?.group_type].push(item);
    return acc;
  }, {});

  useEffect(() => {
    window.console.log('groupedPeople', blok);
  }, [blok]);

  const groupedPeopleByType = Object.values(groupedPeople);

  // handle filter button click. set filter to the button's value. if the button is already active, set filter to undefined

  const handleFilterButtonClick = (target, location) => {
    document
      .querySelectorAll('[data-button-name="location"]')
      .forEach((el) => el.classList.remove('menu-animate-active'));

    if (location === filter) {
      setFilter(undefined);
      target.classList.remove('menu-animate-active');
      setActiveFilter(false);
    } else {
      setFilter(location);
      target.classList.add('menu-animate-active');
      setActiveFilter(true);
    }
  };

  console.log('activeFilter', activeFilter);

  // create a unique set of location names ordered alphabetically, nested under person.studios.name, where studios is an array of objects
  const locations = blok.people
    .flatMap((person) => person.content?.studios.map((studio) => studio.name))
    .filter((value, index, self) => self.indexOf(value) === index)
    .sort();

  useEffect(() => {
    const handleFilter = (filter) => {
      // only select people who have a studio that matches the filter
      if (filter) {
        const filteredPeople = blok.people.filter((person) =>
          person.content.studios.some((studio) => studio.name === filter)
        );
        setActivePeople(filteredPeople);
      } else {
        setActivePeople(blok.people);
      }
    };

    handleFilter(filter);
  }, [filter, blok.people]);

  const showLocationHandler = (e) => {
    e.target.closest('.toggle-button').classList.toggle('opened');
    setShowLocation(!showLocation);
  };

  useEffect(() => {
    if (blok.animate) {
      sal({ threshold: 0.25 });
    }
  }, [blok.animate]);

  const isAnimated = blok.animate;

  return (
    <div className="container">
      <div className="flex items-start gap-16 pb-8 pt-2.5 lg:gap-16 lg:pb-12 lg:pt-4 xl:gap-24 xl:pb-14 xl:pt-8">
        <div className="flex items-center justify-between gap-6">
          <button
            className="toggle-button relative flex items-center justify-between pr-8 text-sm font-semibold leading-none lg:text-base xl:text-[1.375rem]"
            type="button"
            onClick={showLocationHandler}
          >
            Studio Location
            <span className="toggle-icon-plus absolute right-0">
              <PlusIcon isMinus={false} className="h-[0.85rem] w-[0.85rem]" />
            </span>
            <span className="toggle-icon-minus absolute right-0">
              <PlusIcon className="h-[0.625rem] w-[0.625rem]" isMinus />
            </span>
          </button>
        </div>

        <AnimatePresence>
          {showLocation && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              transition={{ duration: 0.5, type: 'tween' }}
            >
              <ul className="space-y-2 text-sm leading-none lg:text-lg xl:-mt-[0.15rem] xl:space-y-3 xl:text-2xl">
                {locations.map((location, index) => (
                  <li key={index}>
                    <button
                      type="button"
                      className="menu-animate"
                      data-button-name="location"
                      onClick={(e) => handleFilterButtonClick(e.target, location)}
                    >
                      {location}
                    </button>
                  </li>
                ))}
              </ul>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {groupedPeopleByType.map((group, idx) => (
        <div className="grid grid-cols-2 gap-5 py-2.5 lg:grid-cols-3 xl:gap-8 xl:py-4" key={idx}>
          <AnimatePresence>
            {group.map((person) => {
              const extraImage = person.content?.extra_media;
              const gridLayout = person.content?.grid_layout;

              let gridClass = 'col-span-full lg:col-span-2 lg:row-span-2';

              if (gridLayout === 'grid-2-full') {
                gridClass = 'col-span-full order-1 lg:col-[2_/_-1] lg:row-span-2';
              }
              const hasVideo =
                person?.content?.extra_media_video?.filename !== undefined &&
                person?.content?.extra_media_video?.filename !== '';

              return (
                <Fragment key={person.id}>
                  {!activeFilter &&
                    extraImage?.filename !== '' &&
                    extraImage &&
                    extraImage?.filename !== null && (
                      <div
                        className={gridClass}
                        {...(isAnimated
                          ? {
                              'data-sal': 'slide-up',
                              'data-sal-duration': '1000',
                              'data-sal-delay': '0',
                              'data-sal-easing': 'ease',
                            }
                          : {})}
                      >
                        {hasVideo ? (
                          <ResVideo
                            mobileVideo={person.content.extra_media_video}
                            desktopVideo={person.content.extra_media_video}
                            mobilePoster={person.content.extra_media}
                            desktopPoster={person.content.extra_media}
                          />
                        ) : (
                          <ResImg
                            mobile={person.content.extra_media}
                            desktop={person.content.extra_media}
                            critical
                          />
                        )}
                      </div>
                    )}
                  <motion.div
                    initial={{ opacity: 0, scale: 0.95 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0, scale: 0.95 }}
                    transition={{ duration: 0.5, type: 'tween' }}
                    layout
                  >
                    <CtaCard
                      data={person.content}
                      slug={person.full_slug}
                      isAnimated={isAnimated}
                      aspectRatioClass="aspect-[520/320]"
                      mediaWrapperClass="aspect-[520/320] !overflow-visible !flex-none"
                      maxWidth={650}
                      hasTag
                    />
                  </motion.div>
                </Fragment>
              );
            })}
          </AnimatePresence>
        </div>
      ))}
    </div>
  );
};

export default AllPeople;

AllPeople.propTypes = {
  blok: PropTypes.object.isRequired,
};
